import React, { useRef, useState } from "react";

import { Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import LoadingModal from "@/common/LoadingModal";
import TriUploadFile from "@/common/TriUploadFile";
import { acceptsFile } from "@/components/alerts/add-v2/media/acceptsFile";
import { importBulletinFromPdf } from "@/components/alerts/add-v2/persistence/pdfImport";

/**
 * Two spellings of the same rule, because they are read by different things.
 *
 * `TriUploadFile` compares its `accept` prop with `===` when it filters a drop or a paste, so it has
 * to be exactly one of the three strings its type allows. The guard below also takes the extension,
 * for a file the picker returned with no `type` at all.
 */
const TRI_ACCEPT = "application/pdf" as const;
const ACCEPTED = "application/pdf,.pdf";

interface UploadBulletinDialogProps {
  open: boolean;
  onClose: () => void;
  /** The draft the extraction created. The caller decides where to send the officer. */
  onImported: (bulletinId: number) => void;
}

/**
 * The "upload an existing bulletin" entry.
 *
 * Reuses the production tri-box upload (device / paste / drag-and-drop) so this path looks the same
 * as every other upload in the portal, and the production `LoadingModal` while the AI reads the
 * file — the wait is 10 to 30 seconds and it is the same wait the old flow shows.
 *
 * The whole gesture lives here, extraction included, because **it creates a bulletin**: a caller
 * that only received a `File` would have to own the loading state, the two failure modes and the
 * navigation, which is three quarters of this component. What is left to the caller is the one
 * decision it cannot make — where the officer goes next.
 *
 * While the extraction is in flight the dialog **cannot be closed**. A row is being created and its
 * id is only in this promise; letting the officer walk away would leave a draft nothing on screen
 * ever mentioned.
 */
export const UploadBulletinDialog: React.FC<UploadBulletinDialogProps> = ({
  open,
  onClose,
  onImported,
}) => {
  const { t } = useTranslation("bulletin/create-v2");
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  // `TriUploadFile` filters a drop and a paste itself; the file picker's own `accept` is advisory,
  // so the one route that can hand over anything at all is checked here.
  const handleFile = async (file: File) => {
    if (!acceptsFile(file, ACCEPTED)) {
      toast.error(t("start.upload_not_pdf"));
      return;
    }

    setBusy(true);
    try {
      const { bulletinId, imagesFailed } = await importBulletinFromPdf(file);
      if (imagesFailed) toast.warning(t("start.upload_images_failed"));
      onImported(bulletinId);
    } catch (error) {
      console.error("Failed to import a bulletin from a PDF:", error);
      toast.error(t("start.upload_failed"));
    } finally {
      setBusy(false);
    }
  };

  const stages = [
    t("start.extract_stage_reading"),
    t("start.extract_stage_details"),
    t("start.extract_stage_location"),
    t("start.extract_stage_contacts"),
    t("start.extract_stage_images"),
  ];

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}
      >
        <Box sx={{ flex: 1 }}>{t("start.upload_dialog_title")}</Box>
        <IconButton
          onClick={onClose}
          disabled={busy}
          aria-label={t("start.upload_dialog_close")}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {busy ? (
          <LoadingModal
            stages={stages}
            timingMessage={t("start.extract_timing")}
          />
        ) : (
          <TriUploadFile
            uploadMessage={t("start.upload_title")}
            subMessage={t("start.upload_body")}
            fileInput={fileInput}
            accept={TRI_ACCEPT}
            multiple={false}
            handleFileUpload={(_event, files: FileList | null) => {
              const file = files?.[0];
              if (file) void handleFile(file);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
